import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Masthead from "../components/Masthead";
import { api } from "../lib/api";
import { BarChart3, ArrowRight, Newspaper, Archive } from "lucide-react";
import StatsModal from "../components/StatsModal";

function PuzzleCard({ p, muted = false }) {
  const publish = new Date(p.publish_date + "T00:00:00");
  return (
    <Link
      to={`/play/${p.id}`}
      className={`group block p-6 hover:bg-[color:var(--paper)] transition-colors`}
      data-testid={`puzzle-card-${p.id}`}
    >
      <div className="flex items-center justify-between text-[11px] font-mono-plex uppercase tracking-widest" style={{ color: "var(--muted)" }}>
        <span>{p.word_length} letters · {p.max_guesses} guesses</span>
        <span>{publish.toLocaleDateString("en-US", { month: "short", day: "numeric", year: muted ? "numeric" : undefined })}</span>
      </div>
      <h3 className="mt-2 font-serif-display text-2xl sm:text-3xl leading-tight group-hover:underline underline-offset-4">
        {p.title}
      </h3>
      {p.byline && (
        <p className="mt-1 font-mono-plex text-[11px] uppercase tracking-widest" style={{ color: "var(--muted)" }}>
          By {p.byline}
        </p>
      )}
      {p.hint && (
        <p className="mt-2 text-sm italic" style={{ color: "var(--muted)" }}>
          Hint: {p.hint}
        </p>
      )}
      <div className="mt-4 flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest">
        Play now <ArrowRight size={14} />
      </div>
    </Link>
  );
}

function PuzzleGrid({ puzzles, testId }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-[color:var(--ink-navy)] bg-white" data-testid={testId}>
      {puzzles.map((p, idx) => {
        const isRightCol = idx % 2 === 1;
        const rowsCount = Math.ceil(puzzles.length / 2);
        const rowOfThis = Math.floor(idx / 2);
        const isLastRow = rowOfThis === rowsCount - 1;
        return (
          <div
            key={p.id}
            className={`
              ${!isRightCol ? "md:border-r-2" : ""}
              ${!isLastRow ? "border-b-2" : ""}
              border-[color:var(--ink-navy)]
            `}
          >
            <PuzzleCard p={p} />
          </div>
        );
      })}
    </div>
  );
}

export default function Home() {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsOpen, setStatsOpen] = useState(false);

  useEffect(() => {
    api.get("/puzzles").then((r) => {
      setPuzzles(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const { todays, archive } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const t = [];
    const a = [];
    for (const p of puzzles) {
      if (p.publish_date === today) t.push(p);
      else a.push(p);
    }
    return { todays: t, archive: a };
  }, [puzzles]);

  return (
    <div className="paper-texture min-h-screen">
      <Masthead />

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif-display text-3xl sm:text-4xl" data-testid="puzzles-heading">
            Today's Puzzles
          </h2>
          <button
            className="btn-editorial ghost flex items-center gap-2"
            onClick={() => setStatsOpen(true)}
            data-testid="open-stats-btn"
          >
            <BarChart3 size={16} /> Stats
          </button>
        </div>

        <div className="rule-thick mb-6" />

        {loading ? (
          <p className="font-mono-plex text-sm" style={{ color: "var(--muted)" }}>
            Setting the press…
          </p>
        ) : todays.length === 0 && archive.length === 0 ? (
          <div className="border-2 border-[color:var(--ink-navy)] p-10 text-center bg-white" data-testid="empty-state">
            <Newspaper className="mx-auto mb-4" size={40} />
            <p className="font-serif-display text-2xl">No puzzles in print yet.</p>
            <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
              Check back once our editor sets the next word.
            </p>
          </div>
        ) : todays.length === 0 ? (
          <div className="border-2 border-[color:var(--ink-navy)] p-8 text-center bg-white" data-testid="no-today-state">
            <p className="font-serif-display text-2xl">No new edition today.</p>
            <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
              Browse the archive below or come back tomorrow.
            </p>
          </div>
        ) : (
          <PuzzleGrid puzzles={todays} testId="puzzle-list" />
        )}

        {archive.length > 0 && (
          <section className="mt-16" data-testid="archive-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-display text-3xl sm:text-4xl flex items-center gap-3">
                <Archive size={28} /> The Archive
              </h2>
              <span className="font-mono-plex text-xs uppercase tracking-widest" style={{ color: "var(--muted)" }}>
                {archive.length} back issue{archive.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="rule-thick mb-6" />
            <PuzzleGrid puzzles={archive} testId="archive-list" />
          </section>
        )}

        <div className="mt-16 border-t-2 border-[color:var(--ink-navy)] pt-6 text-center">
          <p className="font-mono-plex text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--muted)" }}>
            © The Nevada Sagebrush · Reno, NV · A word for every issue
          </p>
        </div>
      </main>

      <StatsModal open={statsOpen} onClose={() => setStatsOpen(false)} />
    </div>
  );
}
